import { createTheme, PaletteMode } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Mode } from '../constants/enums';

export const isLuminance = (hex: string): boolean => {
  let color = hex.replace(/^#/, '');

  if (color.length === 3) {
    color = color
      .split('')
      .map(char => char + char)
      .join('');
  }
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};
export const getContrastTextColor = (
  hex: string,
  darkColor = '#000',
  lightColor = '#fff',
): string => {
  return isLuminance(hex) ? darkColor : lightColor;
};

const adjust = (outerColor: string, amount: number): string => {
  if (outerColor === '#000') {
    return '#000';
  }
  if (outerColor === '#fff') {
    return '#fff';
  }
  return `#${outerColor
    .replace(/^#/, '')
    .replace(/../g, color =>
      `0${Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)}`.substr(-2),
    )}`;
};

const generateStyles = (mainColor: string): string => `
  :root {
     --color-primary: ${mainColor};
     --color-primary-dark: ${adjust(mainColor, -30)};
     --color-primary-light: ${adjust(mainColor, 30)};
     --icon-color-primary: white;
     --color-primary-white-shade: #cadfd7;
   }
`;

const addStyles = (mainColor: string): void => {
  const style = document.createElement('style');
  style.textContent = generateStyles(mainColor);
  document.head.appendChild(style);
};

export const createThemeFromConfig = (
  mainColor = '#000',
  textColor: string,
  mode: PaletteMode,
): Theme => {
  addStyles(mainColor);
  const isDark = mode === Mode.Dark;

  const contrastMain = adjust(mainColor, isDark ? -50 : 50);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#000' : mainColor,
        light: contrastMain,
        dark: contrastMain,
        contrastText: textColor,
      },
      secondary: {
        main: isDark ? '#656565' : '#fff',
        contrastText: getContrastTextColor(mainColor),
      },
      text: {
        primary: isDark ? '#fff' : '#000',
        secondary: isDark ? '#cfcfcf' : '#6e6e6e',
      },
      background: {
        default: isDark ? '#464646' : '#D0D0D0',
        paper: isDark ? '#656565' : '#fff',
      },
    },
    typography: {
      fontFamily: 'Nunito Sans',
    },
    components: {
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            color: isDark ? '#D0D0D0' : '#171717',
            '& .MuiBreadcrumbs-li': {
              cursor: 'pointer',
              color: isDark ? '#ffffff' : '#171717',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& :checked': {
              color: 'red',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: isDark ? '#fff' : '#000',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            padding: '5px 6px',
            color: isDark ? '#fff' : '#000',
            borderColor: contrastMain,
            textTransform: 'capitalize',
            '& path': {
              fill: contrastMain,
            },
            '& .MuiButton-endIcon': {
              marginLeft: '2px',
            },
          },
          outlined: {
            fontWeight: 700,
          },
          contained: {
            color: isDark ? '#fff' : getContrastTextColor(mainColor),
            backgroundColor: isDark ? '#000' : mainColor,
            '&:hover': {
              backgroundColor: isDark ? '#000' : mainColor,
            },
            '& path': {
              fill: isDark ? '#fff' : getContrastTextColor(mainColor),
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'capitalize',
            padding: '5px 15px',
            fontSize: '16px',
            lineHeight: 1,
            '&.Mui-selected': {
                background:
                  mode === Mode.Dark
                    ? '#000'
                    : mainColor,
                color:
                  mode === Mode.Dark
                    ? '#fff'
                    : getContrastTextColor(mainColor),
              '&:hover': {
                backgroundColor: mode === Mode.Dark
                  ? '#464646' : mainColor,
              },
            },
          },
        },
      },
    },
  });
};

export default {};
